<template>
  <article :class="$style.card" data-testid="property-ownership-card">
    <div :class="[$style.illustrationPanel, isHgPlatform() && $style.hgIllustrationPanel]">
      <HgHouseIllustration v-if="isHgPlatform()" :class="$style.illustration" />
      <Is24HouseIllustration v-else :class="$style.illustration" />
    </div>
    <div :class="$style.content">
      <template v-if="mode === 'question'">
        <div :class="$style.textWrapper">
          <h2 :class="$style.title">{{ t('favourites.page.propertyOwnership.title') }}</h2>
          <p :class="$style.description">
            {{ t('favourites.page.propertyOwnership.description') }}
          </p>
        </div>
        <div :class="$style.actions">
          <HgButton
            :class="$style.actionButton"
            :disabled="isSaving"
            data-testid="property-ownership-yes"
            theme="secondary-bordered"
            @click="emit('select', true)"
          >
            {{ t('favourites.page.propertyOwnership.yes') }}
          </HgButton>
          <HgButton
            :class="$style.actionButton"
            :disabled="isSaving"
            data-testid="property-ownership-no"
            theme="secondary-bordered"
            @click="emit('select', false)"
          >
            {{ t('favourites.page.propertyOwnership.no') }}
          </HgButton>
        </div>
      </template>

      <template v-else>
        <div :class="$style.textWrapper">
          <HgBadge
            small
            :class="$style.badgeWrap"
            data-testid="property-ownership-how-cta-badge"
            :theme="isHgPlatform() ? 'secondary' : 'green'"
            :text="t('favourites.page.propertyOwnership.howCtaBadge')"
          />
          <h2 :class="$style.title">
            {{ t('favourites.page.propertyOwnership.howCtaHeadline') }}
          </h2>
          <i18n-t
            keypath="favourites.page.propertyOwnership.howCtaBody.mainText"
            tag="p"
            :class="$style.description"
          >
            <template v-if="te('favourites.page.propertyOwnership.howCtaBody.emphasis')" #emphasis>
              <strong>{{ t('favourites.page.propertyOwnership.howCtaBody.emphasis') }}</strong>
            </template>
          </i18n-t>
        </div>
        <div :class="$style.actions">
          <HgButton
            data-testid="homeowner-world-cta-add-property"
            theme="primary"
            :disabled="isSaving"
            @click="emit('addProperty')"
          >
            {{ t('favourites.page.propertyOwnership.howCtaPrimary') }}
          </HgButton>
          <HgLink
            :href="learnMoreUrl"
            data-testid="homeowner-world-cta-learn-more"
            target="_blank"
            rel="noopener noreferrer"
            @click="emit('learnMore')"
          >
            {{ t('favourites.page.propertyOwnership.howCtaLearnMore') }}
          </HgLink>
        </div>
      </template>
    </div>
  </article>
</template>

<script setup lang="ts">
import HgHouseIllustration from '@/assets/hg/house.svg';
import Is24HouseIllustration from '@/assets/is24/house.svg';
import { isHgPlatform } from '@/helpers/envService';
import {
  InteractionContainerName,
  InteractionEvent,
  InteractionGoal,
  trackEvent,
} from '@/helpers/GTM/GTMHelper';
import { HgBadge, HgButton, HgLink } from '@smg-real-estate/domus';
import { onMounted } from 'vue';
import { useI18n } from 'vue-i18n';

const props = withDefaults(
  defineProps<{
    mode?: 'question' | 'cta';
    isSaving?: boolean;
    learnMoreUrl?: string;
  }>(),
  {
    mode: 'question',
    isSaving: false,
    learnMoreUrl: '',
  },
);

const emit = defineEmits<{
  select: [isHomeowner: boolean];
  addProperty: [];
  learnMore: [];
}>();

const { t, te } = useI18n();

onMounted(() => {
  trackEvent(InteractionEvent.FEATURE_DISPLAYED, {
    h_event_action_007:
      props.mode === 'question'
        ? InteractionGoal.HOW_DISPLAY_PROPERTY_OWNERSHIP_QUESTION
        : InteractionGoal.HOW_DISPLAY_HOMEOWNER_BANNER,
    h_event_label_008: InteractionContainerName.FAVOURITES,
  });
});
</script>

<style lang="scss" module>
@import '@/assets/variables';

.card {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: $color-white;
  border-radius: $space-50;
  box-shadow: $elevation-minimal;
}

.illustrationPanel {
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: $space-800;
  padding: $space-100;
  background: #e7fef1;
}

.hgIllustrationPanel {
  background: #ffe6f4;
}

.illustration {
  width: 100%;
  height: auto;
}

.hgIllustrationPanel .illustration {
  max-width: 10.2rem;
}

.illustrationPanel:not(.hgIllustrationPanel) .illustration {
  max-width: $space-600;
}

.content {
  display: flex;
  flex-direction: column;
  gap: $space-100;
  padding: $space-150;
}

.badgeWrap {
  display: inline-block;
  margin-bottom: $space-50;
}

.title {
  margin: 0 0 $space-50;
  color: $color-grey-120;

  @include body-2-bold;
}

.description {
  margin: 0;
  color: $color-grey-120;

  @include body-2;
}

.actions {
  display: flex;
  flex-direction: column;
  gap: $space-100;
  align-items: flex-start;
}

.actionButton {
  width: 100%;
}

@media (min-width: $viewport-md-min) {
  .card {
    flex-direction: row;
  }

  .illustrationPanel {
    flex: 0 0 $listing-card-media-column-width;
    min-height: $space-800;
  }

  .content {
    flex: 1;
    justify-content: space-between;
    padding: $space-150;
  }

  .actions {
    flex-direction: row;
    gap: $space-100;
    align-items: center;
  }

  .actionButton {
    width: auto;
  }
}
</style>
